import * as React from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectWidget } from '../../../../../widgets/select/SelectWidget';
import { OrganizationModel } from '@journeyapps-platform/lib-journey-core';
import { IntegrationKind, IntegrationState } from '@journeyapps-platform/types-cerberus';
import { observer } from 'mobx-react';
import { styled } from '../../../../../theme';
import { ButtonWidget } from '../../../../../widgets/buttons/ButtonWidget';

export interface AzureProjectDropDownWidgetProps {
  org: OrganizationModel;
  azureProject?: string;
  azureProjectChanged: (org: string, project: string) => any;
  createIntegration: () => any;
}

namespace S {
  export const Icon = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.text.primary};
  `;

  export const LoadingContainer = styled.div`
    display: flex;
    flex-direction: row;
  `;

  export const LoadingText = styled.div`
    margin-left: 10px;
    opacity: 0.5;
    color: ${(p) => p.theme.text.primary};
  `;

  export const SelectContainer = styled.div<{ padTop?: boolean }>`
    width: 100%;
    color: ${(p) => p.theme.text.primary};
    margin-top: ${(p) => (p.padTop ? 16 : 0)}px;
  `;

  export const SelectLabel = styled.div`
    color: ${(p) => p.theme.text.secondary};
    padding-bottom: 8px;
  `;

  export const Link = styled.p`
    padding-top: 16px;
    padding-bottom: 16px;
    color: ${(p) => p.theme.text.primary};
    font-size: 12px;
  `;

  export const A = styled.span`
    color: ${(p) => p.theme.text.highlight};
    cursor: pointer;
  `;

  export const Button = styled(ButtonWidget)`
    margin-top: 10px;
  `;
}

export const AzureProjectDropDownWidget: React.FC<AzureProjectDropDownWidgetProps> = observer((props) => {
  const [loading, setLoading] = useState(true);
  const azureIntegrations = props.org.integrations.items
    .filter((m) => m.model.state === IntegrationState.Connected)
    .filter((i) => i.model.kind === IntegrationKind.AzureDevops);

  useEffect(() => {
    props.org.loadIntegrations().then(() => {
      setLoading(false);
    });
  }, []);

  if (azureIntegrations.length === 0) {
    if (loading) {
      return (
        <S.LoadingContainer>
          <S.Icon icon="spinner" spin={true} />
          <S.LoadingText>Loading Azure DevOps projects...</S.LoadingText>
        </S.LoadingContainer>
      );
    }
    return (
      <div>
        <S.LoadingText>No Azure DevOps project found.</S.LoadingText>
        <S.Button
          action={() => {
            props.createIntegration();
          }}
          label="Add Integration"
        />
      </div>
    );
  }

  // group the results into org / personal account
  const groups = _.chain(azureIntegrations)
    .groupBy((g) => g.getProperties<IntegrationKind.AzureDevops>().organization)
    .map((accounts, org) => {
      return {
        label: org,
        options: accounts.map((integration) => {
          const properties = integration.getProperties<IntegrationKind.AzureDevops>();
          return {
            value: integration.model.id,
            label: properties.project
          };
        })
      };
    })
    .value();

  return (
    <S.SelectContainer>
      <S.SelectLabel>{`Azure DevOps Project`}</S.SelectLabel>
      <SelectWidget
        onChange={(v: any) => {
          const i = azureIntegrations.find((i) => i.model.id === v.value)!;
          const properties = i.getProperties<IntegrationKind.AzureDevops>();
          props.azureProjectChanged(properties.organization, properties.project);
        }}
        defaultInputValue={props.azureProject}
        defaultValue={
          props.azureProject
            ? {
                value: props.azureProject,
                label: props.azureProject
              }
            : null
        }
        placeholder="Select Azure DevOps Project"
        options={groups}
      />
      <S.Link>
        If you don't see your project in the list above,{' '}
        <S.A
          onClick={() => {
            props.createIntegration();
          }}
        >
          you will need to add an Azure DevOps integration
        </S.A>
      </S.Link>
    </S.SelectContainer>
  );
});
